import apiClient from './client';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  modelUsed?: string;
  responseTimeMs?: number;
  createdAt: string;
}

export interface ChatSession {
  id: string;
  title: string;
  status: 'active' | 'archived';
  messageCount: number;
  lastMessageAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatSessionWithMessages {
  session: ChatSession;
  messages: ChatMessage[];
}

export interface ChatSessionsListResponse {
  sessions: ChatSession[];
  total: number;
  limit: number;
  offset: number;
}

export const chatSessionsApi = {
  /**
   * List all chat sessions for the current user
   */
  async listSessions(options?: {
    limit?: number;
    offset?: number;
  }): Promise<ChatSessionsListResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());

    const response = await apiClient.get<ChatSessionsListResponse>(
      `/chat/sessions${params.toString() ? `?${params.toString()}` : ''}`
    );
    return response.data;
  },

  /**
   * Get a specific chat session with all its messages
   */
  async getSession(sessionId: string): Promise<ChatSessionWithMessages> {
    const response = await apiClient.get<ChatSessionWithMessages>(
      `/chat/sessions/${sessionId}`
    );
    return response.data;
  },

  /**
   * Update a chat session (e.g., rename title)
   */
  async updateSession(
    sessionId: string,
    data: { title: string }
  ): Promise<{ session: ChatSession }> {
    const response = await apiClient.put<{ session: ChatSession }>(
      `/chat/sessions/${sessionId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete (archive) a chat session
   */
  async deleteSession(sessionId: string): Promise<{ success: boolean }> {
    const response = await apiClient.delete<{ success: boolean }>(
      `/chat/sessions/${sessionId}`
    );
    return response.data;
  },

  /**
   * Clear all chat sessions
   */
  async clearAllSessions(): Promise<{ success: boolean; count: number }> {
    const response = await apiClient.delete<{ success: boolean; count: number }>(
      '/chat/sessions'
    );
    return response.data;
  },
};

export default chatSessionsApi;
